import * as React from "react"
import { type PreviewShareQuery } from "@digits-graphql/frontend/graphql-public"
import { SvgDigitsLogoNoBackground } from "@digits-shared/components/SVGIcons/digits/DigitsLogoNoBackground.svg"
import { SvgDigitsShield } from "@digits-shared/components/SVGIcons/digits/DigitsShield.svg"
import { Input } from "@digits-shared/components/UI/Elements/Form/Input"
import borders from "@digits-shared/themes/borders"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import { AnimatePresence, m } from "framer-motion"
import styled, { css } from "styled-components"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"

export function getLegalEntityId(preview: PreviewShareQuery) {
  return preview.previewShare.legalEntity.id
}

export const Logo = styled(SvgDigitsLogoNoBackground)`
  width: 156px;
  height: 156px;
  flex-shrink: 0;
  fill: ${colors.logoGreen};
  align-self: center;
  padding: 8px;
`

export const WelcomeContainer = styled.div`
  font-size: 22px;
  font-weight: ${fonts.weight.light};
  line-height: 2;
  text-align: center;

  b {
    font-weight: ${fonts.weight.medium};
  }
`

export const MESSAGE_SWITCH_TIMEOUT = 200

export const MESSAGE_SWITCH_CLASS_NAME = "message"

export const MessageOrError: React.FC<{ className?: string; children?: React.ReactNode }> = ({
  className,
  children,
}) => {
  const {
    state: { errorText },
  } = useOnboardingContext()
  const content = errorText || children

  const key = errorText || ""

  return (
    <AnimatePresence mode="wait">
      <StyledMessage
        key={key}
        className={className}
        $error={!!errorText}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {content}
      </StyledMessage>
    </AnimatePresence>
  )
}

const StyledMessage = styled(m.div)<{ variableHeight?: boolean; $error: boolean }>`
  margin-bottom: 32px;
  line-height: 22px;
  font-size: ${({ $error }) => ($error ? "14px" : "16px")};
  font-weight: ${({ $error }) => ($error ? fonts.weight.normal : fonts.weight.book)};
  color: ${({ $error }) => ($error ? colors.orange : colors.secondary)};
  text-align: center;
`

export const StepTransition = styled(m.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const EmailAddressInput = styled(Input)`
  cursor: pointer;
  text-align: center;
  border: 1px solid ${colors.translucentSecondary40};
  border-radius: ${borders.radius.input};

  &:hover:not(:disabled) {
    border: 1px solid ${colors.secondary};
  }

  &::placeholder {
    color: ${colors.secondary};
    text-transform: uppercase;
  }

  :not(:placeholder-shown) {
    text-align: left;
  }
`

export const Links = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const NavLink = styled.button`
  margin-top: 18px;
  padding: 0;
  font-size: 11px;

  font-weight: ${fonts.weight.medium};
  color: ${colors.secondary};
  border: none;
  background-color: ${colors.transparent};
  transition: color 250ms ease-out;
  cursor: pointer;
  outline: none;
  justify-self: flex-start;

  &:hover {
    color: ${colors.secondary};
  }
`

const SecuredWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: ${fonts.weight.light};
  color: ${colors.secondary80};
  margin-top: auto;
  margin-bottom: 16px;

  b {
    font-weight: ${fonts.weight.medium};
    margin-right: 5px;
  }
`

const DigitsShield = styled(SvgDigitsShield)<{ $shieldSize: number }>`
  ${({ $shieldSize }) => css`
    width: ${$shieldSize}px;
    height: ${$shieldSize}px;
  `};
  margin-right: 8px;
  fill: ${colors.secondary80};
`

const EmailLinkText = styled.a`
  text-decoration: underline;
`

export const SecuredByDigits: React.FC<{ shieldSize?: number }> = ({ shieldSize = 24 }) => (
  <SecuredWrapper>
    <DigitsShield $shieldSize={shieldSize} /> <b>Secured</b> by Digits
  </SecuredWrapper>
)

export const EmailLink: React.FC<React.PropsWithChildren> = ({ children }) => (
  <EmailLinkText href={`mailto:${children}`}>{children}</EmailLinkText>
)
